/**
 * ProtectedRoute Component
 *
 * Wrapper component for authenticated routes.
 * Features:
 * - Check authentication status via useAuth hook
 * - Redirect to /login if not authenticated
 * - Pass children through if authenticated
 * - Show loading state during authentication check
 *
 * @see specs/002-frontend-mvp/spec.md - User Story 1 (T039)
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';

// ============================================================================
// Props Interface
// ============================================================================

interface ProtectedRouteProps {
  /**
   * Child components to render if authenticated
   */
  children: ReactNode;
}

// ============================================================================
// ProtectedRoute Component
// ============================================================================

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div
            className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"
            role="status"
            aria-label="Loading"
          >
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the intended destination in location state
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
}

export default ProtectedRoute;
